import { Resource, Verb } from '../../api/authz';

// 与原型 PROCESS_ARCHS / MODULES 同步, 一个菜单一条记录.
// subs[i].path 是该子页的完整 URL; subs[0].path 通常 = menu.path.

export interface SubMenu {
    label: string;
    path: string;
    /** 子菜单图标 (emoji). 只在数据资产这类"多个平行中心"的分组里用, 帮着一眼区分. 可不填. */
    icon?: string;
    /** 用于 match 当前路由属于哪个子页 (动态参数路由用). 默认按 path 精确匹配. */
    matcher?: (currentPath: string) => boolean;
    /**
     * 该 sub 是否可用. 用于"详情"类子页 - 当当前路径不在该详情下时, 没有具体目标可跳, 应禁用.
     * 返回 false 时 Sidebar 会把该二级菜单渲染为灰色不可点状态.
     */
    enabled?: (currentPath: string) => boolean;
    /** 禁用时鼠标 hover 的提示. */
    disabledHint?: string;
    /**
     * prism 读路径切到本库后隐藏 (2026-10-20): 历史加工数据并入「材料加工」, 独立入口不再需要.
     * 切换前 (readLocal=false) 照常显示.
     */
    hideWhenPrismLocal?: boolean;
    /**
     * 动态路径键. Sidebar.tsx 会按该键从 store 解析真实路径
     * (如 'materialDetailDefault' → /materials/{material.curMaterialId}).
     */
    dynamicPath?: 'materialDetailDefault';
    /** RBAC 资源域: Sidebar 按 useCan(resource, verb ?? 'get') 过滤 (2026-09-01 起唯一口径). */
    resource?: Resource;
    /** 满足任一条即可见 (2026-09-13): 一个页面承载多个资源域时用, 如数据字典按各组归属判. */
    anyOf?: { resource: Resource; verb?: Verb }[];
    /** 判定动作, 默认 list. 管理类入口用 update/manage 区分"看得到"与"改得动". */
    verb?: Verb;
}

export interface MenuItem {
    key: string;
    icon: string;
    label: string;
    /** 点击菜单跳转的默认路径 (通常 = subs[0].path) */
    path: string;
    subs: SubMenu[];
    /** 仅国服镜像站 (SITE_ROLE=replica) 可见 (Sidebar 按 store.me.siteRole 过滤). */
    replicaOnly?: boolean;
    /** 仅无审批治理权的人可见 ("我的工单"; 有权者走「审批中心」全量视图). */
    nonAdminOnly?: boolean;
    /**
     * 只有一个子页也展开二级 (默认单子页不展开, 免得和菜单名重复).
     * 用于「数据资产」这类分组语义明确、后续会持续追加子中心的菜单.
     */
    alwaysExpandable?: boolean;
    /** RBAC 资源域: Sidebar 按 useCan(resource, verb ?? 'get') 过滤. */
    resource?: Resource;
    /** 判定动作, 默认 list. */
    verb?: Verb;
}

export const MENUS: MenuItem[] = [
    {
        key: 'material',
        resource: 'material',
        icon: '📦',
        label: '材料主数据',
        path: '/materials',
        subs: [
            {
                label: '材料库',
                path: '/materials',
                matcher: (p) => p === '/materials',
            },
            {
                label: '材料详情',
                // path 用占位 /materials, Sidebar 会在渲染时按 store 里的 curMaterialId
                // 动态解析成 /materials/{curMaterialId}; 详见 Sidebar.tsx 中的 resolveSubPath.
                path: '/materials',
                matcher: (p) =>
                    p.startsWith('/materials/') &&
                    p !== '/materials/id-mapping' &&
                    p !== '/materials/categories' &&
                    p !== '/materials/templates' &&
                    p !== '/materials/new',
                dynamicPath: 'materialDetailDefault',
            },
            {
                label: 'SKU 库',
                path: '/skus',
                matcher: (p) => p === '/skus',
            },
            {
                // 2026-09-12 从「Schema 管理」拆出: 材料理化字段组模板
                label: '材料模板',
                path: '/materials/templates',
                matcher: (p) => p === '/materials/templates',
                resource: 'phys_schema',
                verb: 'update', // 模板管理页只给管理者; 普通用户不进 (接口 get 仍开放: 材料详情渲染字段要读模板)
            },
            {
                // 2026-10-14: SKU 规格类型 (板材 / 卷材 / 纸张 …) 与字段
                label: 'SKU 规格模板',
                path: '/skus/templates',
                matcher: (p) => p === '/skus/templates',
                resource: 'sku_schema',
                verb: 'update',
            },
        ],
    },
    {
        key: 'device',
        resource: 'device',
        icon: '⚙️',
        label: '设备工况',
        path: '/devices',
        subs: [
            { label: '设备树/工况', path: '/devices' },
            { label: '设备管理', path: '/devices/manage' },
            { label: '功率校准', path: '/devices/calibration' },
            { label: 'YAML预览', path: '/devices/yaml' },
            // 2026-09-12 从「Schema 管理」拆出: 设备动态字段组模板
            {
                label: '设备模板',
                path: '/devices/templates',
                resource: 'device_schema',
                verb: 'update',
            },
        ],
    },
    {
        key: 'parameter',
        resource: 'param_row',
        icon: '🧩',
        label: '官方参数中心',
        path: '/param-sets',
        subs: [
            { label: '参数总览', path: '/param-sets' },
            { label: '参数管理', path: '/param-sets/manage' },
            // 2026-09-12 从「Schema 管理」拆出: param_schema 字段模板
            {
                label: '参数模板',
                path: '/param-sets/templates',
                resource: 'param_schema',
                verb: 'update',
            },
        ],
    },
    {
        // 数据资产 (2026-09-01, doc/数据资产/对接设计.md): 各业务平台采集数据的归集中心.
        // 首期只有 UGC 分享中心; 后续商家/商品采集/耗材识别/加工数据各中心在此追加子菜单.
        key: 'assets',
        resource: 'data_asset',
        icon: '🗃️',
        label: '数据资产',
        path: '/assets/ugc-shares',
        // 分组语义: 后续商家/商品采集/耗材识别/加工数据各中心持续追加, 始终以子菜单呈现.
        alwaysExpandable: true,
        subs: [
            {
                icon: '🌐',
                label: 'UGC 分享',
                path: '/assets/ugc-shares',
                // 详情页 /assets/ugc-shares/:id 也归本子菜单高亮
                matcher: (p) =>
                    p === '/assets/ugc-shares' ||
                    p.startsWith('/assets/ugc-shares/'),
            },
            {
                icon: '🔍',
                label: '材料识别',
                path: '/assets/recognitions',
                matcher: (p) =>
                    p === '/assets/recognitions' ||
                    p.startsWith('/assets/recognitions/'),
            },
            {
                icon: '📈',
                label: '材料加工',
                path: '/assets/process-records',
                matcher: (p) =>
                    p === '/assets/process-records' ||
                    p.startsWith('/assets/process-records/'),
            },
            {
                // 满意度统计 (2026-10-23): 官方材料 × 设备维 (机型 × 激光模组) 的推荐参数满意度, 读本库汇总.
                icon: '😊',
                label: '满意度统计',
                path: '/assets/material-satisfaction',
            },
            {
                // 矩阵识别中心 (2026-10-09): Studio 参数矩阵测试图的 AI 逐格打分, 直连 prism matrix_tasks.
                icon: '🔢',
                label: '矩阵识别',
                path: '/assets/matrix-recognitions',
                matcher: (p) =>
                    p === '/assets/matrix-recognitions' ||
                    p.startsWith('/assets/matrix-recognitions/'),
            },
            {
                // 历史加工数据 (2026-09-07): xcs2.7 加工反馈一次性导入, 与「加工数据中心」(Studio 1.9 实时) 并列不混算.
                icon: '🕘',
                label: '历史加工数据',
                path: '/assets/process-history',
                hideWhenPrismLocal: true,
                matcher: (p) =>
                    p === '/assets/process-history' ||
                    p.startsWith('/assets/process-history/'),
            },
            {
                // 商品采集中心 (2026-09-08): 亚马逊 Keepa 商品目录采集, 采集器直写本库; 材质作耗材识别底库来源.
                icon: '🛒',
                label: '商品采集',
                path: '/assets/products',
                matcher: (p) =>
                    p === '/assets/products' ||
                    p.startsWith('/assets/products/'),
            },
            {
                // 商家数据中心 (2026-09-09): c10s 定制商城商家商品, 直读源库不落库; 带可定制区域与加工参数.
                icon: '🏪',
                label: '商家数据',
                path: '/assets/c10s-products',
                matcher: (p) =>
                    p === '/assets/c10s-products' ||
                    p.startsWith('/assets/c10s-products/'),
            },
        ],
    },
    {
        // 人工审核 (2026-09-23 新开的一级分组, 商品打标审核从「数据资产」下迁入).
        //
        // 单开一组的理由: 数据资产那一组是各业务平台采集数据的**归集**中心, 都是"看数据";
        // 这一组是**人工产出**入口 —— 审核员长时间驻留、用键盘逐条裁决, 产出的 final_*
        // 是下游耗材绑定唯一可信的锚。混在归集中心里既不好找, 也把"看"和"产出"当成了一件事。
        //
        // 路径保持 /assets/product-labels 不变: 页面支持把筛选写进 URL 分享给同事分工
        // 认领, 改路径会让已发出去的链接全失效, 而菜单位置与 URL 前缀本就不必一致。
        // RBAC 也仍用 data_asset —— 另造资源域要后端配权限并重新授权, 审核员会当场进不去。
        key: 'review',
        resource: 'data_asset',
        icon: '✅',
        label: '人工审核',
        path: '/assets/product-labels',
        // 分组语义: 人工裁决入口会持续追加(部位补录、效果评估复核等), 始终以子菜单呈现 ——
        // 与「数据资产」同理, 单子页时也出二级, 免得追加第二个时菜单形状突然变样。
        alwaysExpandable: true,
        subs: [
            {
                // 页内分两个 tab: 亚马逊采集(图粒度) / c10s 商家商品(SKU 粒度).
                icon: '🏷️',
                label: '商品打标审核',
                path: '/assets/product-labels',
                matcher: (p) => p === '/assets/product-labels',
            },
            {
                // 商品打标结果关联官方耗材的审核 (2026-10-09)。
                //
                // 排在打标审核之后不是美观问题, 是**依赖顺序**: 这一页的入池对象正是
                // 打标审核产出的 final_* —— 没审过的图根本进不了绑定池。审核员按菜单
                // 从上往下做, 顺序就是数据流向。
                icon: '🔗',
                label: '商品绑定材料',
                path: '/assets/product-materials',
                matcher: (p) => p === '/assets/product-materials',
            },
        ],
    },
    {
        key: 'gallery',
        resource: 'gallery',
        icon: '🖼️',
        label: '效果图资产',
        path: '/gallery',
        subs: [
            {
                label: '效果图',
                path: '/gallery',
                // /gallery/:id 详情页归到 "效果图" tab; 排除 /gallery/matrix
                matcher: (p) =>
                    p === '/gallery' ||
                    (p.startsWith('/gallery/') &&
                        !p.startsWith('/gallery/matrix')),
            },
            {
                label: '矩阵图',
                path: '/gallery/matrix',
                // 2026-07-30 重设计: 数据源 = gallery.matrix_json 非空的材料级矩阵图
                matcher: (p) =>
                    p === '/gallery/matrix' || p.startsWith('/gallery/matrix/'),
            },
        ],
    },
    {
        key: 'toolbox',
        // 2026-09-13: 工具市场页只有管理动作 (注册 / 配置部署 / 切 dev-live / 删除), 收成只给工具管理员;
        // 设备侧绑定换算服务只需要读工具清单 (device 角色带 tool get), 不需要这个入口.
        resource: 'tool',
        verb: 'update',
        icon: '🧰',
        label: '工具平台',
        path: '/tools',
        subs: [
            {
                label: '工具市场',
                path: '/tools',
                resource: 'tool',
                verb: 'update',
            },
        ],
    },
    {
        key: 'governance',
        resource: 'device', // 设备-材料适配 2026-09-12 并入 device 域
        icon: '🛡️',
        label: '规则/适配/治理',
        path: '/id-mappings',
        subs: [
            { label: 'ID映射治理', path: '/id-mappings' },
            { label: '设备适配', path: '/id-mappings/compat' },
        ],
    },
    {
        key: 'apitoken',
        resource: 'api_token',
        icon: '🔑',
        label: 'API Token',
        path: '/api-tokens',
        subs: [{ label: 'Token 管理', path: '/api-tokens' }],
    },
    {
        // 平台文档 (2026-10-08): 平台简介 + SDK 接口目录, 静态内容, 对所有已准入用户可见 (不挂资源域).
        // 2026-11-02 加「应用接口」: C 端应用通道 /app/v1 (utoken) 的对外接口文档.
        key: 'docs',
        icon: '📖',
        label: '平台文档',
        path: '/docs/intro',
        alwaysExpandable: true,
        subs: [
            { label: '平台简介', path: '/docs/intro' },
            { label: 'SDK 接口', path: '/docs/sdk' },
            { label: '应用接口', path: '/docs/app' },
        ],
    },
    {
        // 站点数据同步 (美服→国服镜像, 2026-08-17): 查看人人可见, 操作按钮 admin 才显示.
        // 2026-08-19: 美服 (master) 是源站, 同步页对它没有意义 → 菜单只在 replica 显示;
        // 直接敲 URL 时概览页仍会渲染"当前为源站"空态兜底.
        key: 'datasync',
        resource: 'datasync',
        replicaOnly: true,
        icon: '🔄',
        label: '数据同步',
        path: '/data-sync',
        subs: [
            { label: '同步概览', path: '/data-sync' },
            { label: '同步记录', path: '/data-sync/logs' },
        ],
    },
    {
        // 仅普通用户可见 (2026-08-19): 管理员管理菜单收成 admin-only 后, 普通用户提交的变更/参数工单
        // 需要一个自己的跟踪入口; 页面复用 ApprovalList 的 mineOnly 模式 (只读进度, 不带审批操作).
        // admin 已有「审批中心」全量视图, 不再重复显示此入口 (路由仍保留, 敲 URL 可访问).
        key: 'myapprovals',
        nonAdminOnly: true,
        icon: '📋',
        label: '我的工单',
        path: '/my-approvals',
        subs: [{ label: '我的工单', path: '/my-approvals' }],
    },
    {
        // 我的权限 (2026-09-13): 非系统管理员看自己的角色并申请更多权限; admin 全权不需要.
        key: 'mypermissions',
        nonAdminOnly: true,
        icon: '🛡️',
        label: '我的权限',
        path: '/my-permissions',
        subs: [{ label: '我的权限', path: '/my-permissions' }],
    },
    {
        // 基础数据 (2026-09-12): 字典 / 字段字典 / 小贴士库 —— 被业务对象引用的基础词表, 从「管理员管理」拆出.
        // 查看人人可见 (各子项按 get 判), 写操作在页内按各自资源域的权限判.
        key: 'basedata',
        // 2026-09-13: 词表读接口 public; 本菜单无自身资源, 子页各按 dict update / schema_field get / tip get 判,
        // 子页全不可见时整个菜单不出现 (Sidebar).
        icon: '📚',
        label: '基础数据',
        path: '/admin/data-dict',
        alwaysExpandable: true,
        subs: [
            // 字典维护/加工模式字典/材料类别/工艺类型四 tab (pages/admin/DataDictAdmin.tsx)
            {
                label: '数据字典',
                path: '/admin/data-dict',
                matcher: (p) => p === '/admin/data-dict',
                // 页面按类目分组归属分权 (2026-09-13): 材料属性→材料 / 设备能力→设备 / 参数枚举→工艺参数,
                // 材料类别与工艺类型各按自己的资源; 能维护其中任一类就看得到这个入口.
                anyOf: [
                    { resource: 'material', verb: 'update' },
                    { resource: 'device', verb: 'update' },
                    { resource: 'param_row', verb: 'update' },
                    { resource: 'category', verb: 'update' },
                    { resource: 'cap_type', verb: 'update' },
                    { resource: 'mm_mode', verb: 'update' },
                ],
            },
            {
                // Schema 字段字典 (xtool-schema SSOT 镜像): 所有模板字段的来源白名单
                label: '字段字典',
                path: '/admin/schema-fields',
                matcher: (p) => p === '/admin/schema-fields',
                resource: 'schema_field',
                // 字段字典页只给「字段字典管理员」(schema_field update): 各模块只读 / 核心管理员虽有 get
                // (模板编辑要取字段名与 key), 但不在基础数据里看到这个菜单 (2026-09-13).
                verb: 'update',
            },
            {
                // 加工小贴士条目的唯一真源 (规格 §4.1)
                label: '小贴士库',
                path: '/tips',
                matcher: (p) => p === '/tips',
                resource: 'tip',
            },
        ],
    },
    {
        // 管理员管理 (2026-09-12 收敛为两项): 权限管理 (准入 / 角色 / 权限分配) + 审批中心.
        // 菜单本身按"能否管理人员"判定; Sidebar 只隐藏入口, 直接敲 URL 由 App.tsx 的 <PermissionRoute> 兜底拦截.
        key: 'adminuser',
        resource: 'admin_user',
        verb: 'update',
        icon: '👤',
        label: '管理员管理',
        path: '/admin-users',
        subs: [
            {
                // 2026-09-13: 「用户管理」→「权限管理」(页内三 tab: 管理员 / 用户管理 / 角色管理)
                label: '权限管理',
                path: '/admin-users',
                resource: 'admin_user',
                verb: 'update',
            },
            // 审批中心唯一入口 (有任一资源审批权的人可见; 普通用户走「我的工单」)
            {
                label: '审批中心',
                path: '/admin/approvals',
                resource: 'approval',
                verb: 'update',
            },
            // 助手反馈 (2026-10-14): AI 平台助手的提问与评价, 用来补平台使用指引
            {
                label: '助手反馈',
                path: '/admin/assistant-feedback',
                resource: 'admin_user',
                verb: 'update',
            },
        ],
    },
];

// 按当前 pathname 找出归属菜单 + 子页索引.
export function locate(
    pathname: string,
): { menu: MenuItem; subIdx: number } | undefined {
    // 两趟: 先在所有菜单里找精确的 sub 匹配, 再做前缀兜底.
    // (不能边遍历边兜底 — 前缀兜底会抢走别的菜单的精确匹配, 如 material 菜单的 '/materials'
    //  前缀会把后面菜单的 /materials/* 子页全部兜住.)
    for (const menu of MENUS) {
        for (let i = 0; i < menu.subs.length; i++) {
            const sub = menu.subs[i];
            if (sub.matcher) {
                if (sub.matcher(pathname)) return { menu, subIdx: i };
            } else if (sub.path === pathname) {
                return { menu, subIdx: i };
            }
        }
    }
    // 兜底: 仅匹配主菜单 path (动态详情等)
    for (const menu of MENUS) {
        if (pathname === menu.path || pathname.startsWith(menu.path + '/')) {
            return { menu, subIdx: 0 };
        }
    }
    return undefined;
}
